import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Portal from '@mui/material/Portal';
import useMediaQuery from '@mui/material/useMediaQuery';

import CircularSpinner from 'shared/atoms/CircularSpinner';
import HorizontalRule from 'shared/atoms/HorizontalRule';
import Button from 'shared/atoms/Button';
import Select from 'shared/atoms/Select';
import Snackbar from 'shared/atoms/Snackbar';
import T from 'shared/atoms/Typography';
import Info from 'shared/molecules/Info';

import theme from 'shared/themes/default';

import { usePlaylist } from './context';
import Dialogs from './components/Dialogs';
import PlaylistOverview from './components/PlaylistOverview';

const BlockWrapper = styled.div`
  padding: 10px 0px 10px 0px;
`;

const ManagerSelectWrapper = styled.div`
  align-items: center;
  display: flex;
  column-gap: ${theme.spacing.s};
  ${theme.breakpoints.mobile} {
    width: 100%;
  }
`;

const PlaylistAdministration = ({ onClickSubscribe, isBackOffice }) => {
  const { t } = useTranslation();

  const {
    dialogs: {
      reminderStatus,
      errorDetailPublish,
      errorMessagePublish,
      successPublish,
      setErrorMessagePublish,
      setSuccessPublish,
      setReminderStatus,
    },
    playlists: { listOwnedPlaylists, loadingPlaylists, playlistManagers },
    handleSelectManager,
    hasSubscription,
    isGlobalOrganization,
    isSubscriptionAdmin,
    managerSelected,
    userId,
  } = usePlaylist();

  const isMobile = useMediaQuery(theme.breakpoints.mobile);

  const managerOptions = [];

  if (userId) {
    managerOptions.push({
      key: userId,
      label: t('playlist.myPlaylists'),
      value: userId,
    });
  }

  managerOptions.push(
    { key: 'all', label: t('playlist.allPlaylists'), value: 'all' },
    ...playlistManagers
      .filter(
        (manager) =>
          isSubscriptionAdmin && (!userId || manager.user.id !== userId)
      )
      .map((manager) => ({
        key: manager.user.id,
        label: manager.user.name,
        value: manager.user.id,
      }))
  );

  const defaultValue = managerOptions.find(
    ({ value }) => value === managerSelected
  )
    ? managerSelected
    : 'all';

  return (
    <>
      {/* Block for options when there is no subscription */}
      {!hasSubscription && (
        <>
          <T
            color={theme.palette.lightPure}
            phoneVariant="headingXs"
            sx={{
              marginTop: isMobile ? undefined : theme.spacing.m2,
              marginBottom: isMobile ? theme.spacing.m1 : theme.spacing.m1,
            }}
            variant="headingS"
          >
            {t('administrateUsers.noActiveSubscription')}
          </T>
          {isSubscriptionAdmin ? (
            <>
              <T>{t('administrateUsers.suggestSubscriptionPlaylist')}</T>
              <T>{t('administrateUsers.subscribeAgain')}</T>
              <BlockWrapper>
                <Button
                  ariaLabel={t('administrateUsers.subscribe')}
                  onClick={() => onClickSubscribe()}
                  theme="dark"
                  type="primary"
                >
                  {t('administrateUsers.subscribe')}
                </Button>
              </BlockWrapper>
            </>
          ) : (
            <T>{t('administrateUsers.contactYourAdmin')}</T>
          )}
          <HorizontalRule />
        </>
      )}

      {/* Title and playlist type or manager selector */}
      <T
        color={theme.palette.lightPure}
        id="see-playlist-text"
        phoneVariant="headingXs"
        sx={{
          marginTop: isMobile ? theme.spacing.m3 : theme.spacing.xl,
        }}
        variant="headingS"
      >
        {t('playlist.playlists')}
      </T>
      {isSubscriptionAdmin && !isGlobalOrganization && (
        <>
          <T
            color={theme.palette.lightInteracting}
            sx={{
              marginTop: theme.spacing.xs,
              marginBottom: isMobile ? theme.spacing.m2 : theme.spacing.m3,
            }}
            variant="paragraph"
          >
            {t('playlist.seeAllOrFilter')}
          </T>
          <ManagerSelectWrapper>
            <Select
              id="manager-picker"
              fullWidth={isMobile}
              items={managerOptions}
              name="manager-picker"
              onChange={({ value }) => handleSelectManager(value)}
              hasAction
              hasBackground
              theme="light"
              sx={{
                flex: isMobile ? '1 1 auto' : 'none',
              }}
              value={defaultValue}
            />
            {defaultValue === 'all' && (
              <Info theme="dark">{t('playlist.sortDescription')}</Info>
            )}
          </ManagerSelectWrapper>
        </>
      )}

      {/* Playlist overview */}
      {listOwnedPlaylists && !loadingPlaylists ? (
        <PlaylistOverview isBackOffice={isBackOffice} />
      ) : (
        <CircularSpinner static />
      )}

      {/* Dialogs component */}
      <Dialogs />

      {/* Success and error message snackbars */}
      {successPublish && (
        <Portal>
          <Snackbar
            message={t('playlist.publishSuccess')}
            onClose={() => setSuccessPublish(false)}
            open={successPublish}
            type="success"
          />
        </Portal>
      )}
      {errorMessagePublish && (
        <Portal>
          <Snackbar
            detailedErrors={errorDetailPublish}
            message={errorMessagePublish}
            onClose={() => setErrorMessagePublish(false)}
            open={!!errorMessagePublish}
            type="error"
          />
        </Portal>
      )}
      {reminderStatus.open && (
        <Portal>
          <Snackbar
            message={reminderStatus.message}
            onClose={() =>
              setReminderStatus({ ...reminderStatus, open: false })
            }
            open={reminderStatus.open}
            type={reminderStatus.type}
          />
        </Portal>
      )}
    </>
  );
};

PlaylistAdministration.propTypes = {
  onClickSubscribe: PropTypes.func,
  isBackOffice: PropTypes.bool,
};

export default PlaylistAdministration;
